import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Phone, Plus } from 'lucide-react';
import { AU_STATES, CLUB_ROLE_LABELS, formatMobile, type AdminClub, type ClubStatus } from '@hoopify/shared';
import { api, errorMessage, fieldErrors } from '../../lib/api';
import { formatDate, timeAgo } from '../../lib/format';
import { Alert, Button, Dialog, Field, LoadingBlock } from '../../components/ui';
import { ReasonDialog } from '../../components/admin/AdminLayout';
import { Flag } from './AdminAccountsPage';

const STATUS: Record<ClubStatus, { label: string; tone: 'warn' | 'ok' | 'danger' | 'muted' }> = {
  pending: { label: 'Waiting for a check', tone: 'warn' },
  approved: { label: 'Approved', tone: 'ok' },
  suspended: { label: 'Suspended', tone: 'danger' },
  rejected: { label: 'Rejected', tone: 'muted' },
};

export function ClubStatusFlag({ status }: { status: ClubStatus }) {
  return <Flag tone={STATUS[status].tone}>{STATUS[status].label}</Flag>;
}

export function AdminClubsPage() {
  const [creating, setCreating] = useState(false);
  const list = useQuery({ queryKey: ['admin', 'clubs'], queryFn: () => api.get<AdminClub[]>('/admin/clubs') });
  const waiting = list.data?.filter((c) => c.status === 'pending').length ?? 0;
  return (
    <>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-[24px] font-semibold tracking-[-0.01em]">Clubs</h1>
        <Button onClick={() => setCreating(true)}>
          <Plus className="size-4" aria-hidden /> New club
        </Button>
      </div>
      <p className="mb-4 max-w-[70ch] text-[14px] text-muted">
        Clubs that register wait here until you’ve checked them. Call the mobile number to make sure it’s really their club, then approve or reject. Clubs you make yourself are approved straight away.
      </p>
      {list.isError && <Alert>{errorMessage(list.error)}</Alert>}
      {list.isLoading ? (
        <LoadingBlock />
      ) : list.data && !list.data.length ? (
        <p className="rounded-lg border border-dashed border-line-strong px-6 py-10 text-center text-muted">No clubs yet.</p>
      ) : (
        list.data && (
          <>
            {waiting > 0 && <p className="mb-2 text-[14px] font-medium text-warn">{waiting === 1 ? '1 club is' : `${waiting} clubs are`} waiting for a check.</p>}
            <div className="overflow-x-auto rounded-lg border border-line bg-surface">
              <table className="w-full min-w-[760px] text-left text-[14px]">
                <thead className="border-b border-line text-[13px] text-muted">
                  <tr>
                    <th className="px-4 py-2 font-medium">Club</th>
                    <th className="px-4 py-2 font-medium">Contact</th>
                    <th className="px-4 py-2 font-medium">Mobile</th>
                    <th className="px-4 py-2 text-right font-medium">Teams</th>
                    <th className="px-4 py-2 font-medium">Registered</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {list.data.map((c) => (
                    <tr key={c.id} className="hover:bg-sunken">
                      <td className="px-4 py-2.5">
                        <Link to={`/admin/clubs/${c.id}`} className="font-medium hover:underline">
                          {c.name}
                        </Link>
                        {(c.suburb || c.state) && <span className="text-muted"> · {[c.suburb, c.state].filter(Boolean).join(' ')}</span>}
                        <span className="ml-2">
                          <ClubStatusFlag status={c.status} />
                        </span>
                        {!c.contactConfirmed && (
                          <span className="ml-1">
                            <Flag tone="muted">Email not confirmed</Flag>
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        {c.contactName}
                        <span className="block text-[13px] text-muted">{c.contactEmail}</span>
                      </td>
                      <td className="tnum px-4 py-2.5 whitespace-nowrap">
                        <a href={`tel:${c.mobile}`} className="hover:underline">
                          {formatMobile(c.mobile)}
                        </a>
                      </td>
                      <td className="tnum px-4 py-2.5 text-right">{c.teamCount}</td>
                      <td className="px-4 py-2.5 text-muted" title={formatDate(c.createdAt)}>
                        {timeAgo(c.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )
      )}
      {creating && <CreateClubDialog onClose={() => setCreating(false)} />}
    </>
  );
}

function CreateClubDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [v, setV] = useState({ name: '', contactName: '', contactEmail: '', mobile: '', suburb: '', state: '' });
  const create = useMutation({
    mutationFn: () => api.post<AdminClub>('/admin/clubs', { ...v, state: v.state || null }),
    onSuccess: async (c) => {
      await qc.invalidateQueries({ queryKey: ['admin', 'clubs'] });
      navigate(`/admin/clubs/${c.id}`);
    },
  });
  const f = fieldErrors(create.error);
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV({ ...v, [k]: e.target.value });
  return (
    <Dialog open onClose={onClose} title="New club">
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); create.mutate(); }} className="space-y-4" noValidate>
        <p className="text-[14px] text-muted">The club is approved straight away. Its contact gets an email inviting them to be the club admin.</p>
        {create.isError && !Object.keys(f).length && <Alert>{errorMessage(create.error)}</Alert>}
        <Field label="Club name" value={v.name} onChange={set('name')} error={f.name} autoFocus />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Contact’s name" value={v.contactName} onChange={set('contactName')} error={f.contactName} />
          <Field label="Contact’s email" type="email" value={v.contactEmail} onChange={set('contactEmail')} error={f.contactEmail} />
        </div>
        <Field label="Contact’s mobile" type="tel" value={v.mobile} onChange={set('mobile')} error={f.mobile} placeholder="0412 345 678" />
        <div className="grid grid-cols-[1fr_auto] gap-4">
          <Field label="Suburb (optional)" value={v.suburb} onChange={set('suburb')} error={f.suburb} />
          <StateSelect value={v.state} onChange={(s) => setV({ ...v, state: s })} />
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending}>
            Create and invite
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

export function StateSelect({ value, onChange }: { value: string; onChange: (s: string) => void }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor="club-state" className="label-caps block">
        State
      </label>
      <select id="club-state" className="field-input" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">–</option>
        {AU_STATES.map((s) => (
          <option key={s}>{s}</option>
        ))}
      </select>
    </div>
  );
}

export function AdminClubPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const key = ['admin', 'club', id];
  const club = useQuery({ queryKey: key, queryFn: () => api.get<AdminClub>(`/admin/clubs/${id}`) });
  const [dialog, setDialog] = useState<'reject' | 'suspend' | 'restore' | null>(null);
  const decide = useMutation({
    mutationFn: (b: { decision: string; note?: string }) => api.post<AdminClub>(`/admin/clubs/${id}/decision`, b),
    onSuccess: (c) => {
      qc.setQueryData(key, c);
      void qc.invalidateQueries({ queryKey: ['admin', 'clubs'] });
    },
  });
  if (club.isLoading) return <LoadingBlock />;
  if (club.isError || !club.data) return <Alert>{errorMessage(club.error)}</Alert>;
  const c = club.data;
  return (
    <>
      <Link to="/admin/clubs" className="mb-4 inline-flex items-center gap-1.5 text-[14px] text-muted hover:text-ink">
        <ArrowLeft className="size-4" aria-hidden /> Clubs
      </Link>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <h1 className="text-[24px] font-semibold tracking-[-0.01em]">{c.name}</h1>
        <ClubStatusFlag status={c.status} />
      </div>

      {c.status === 'pending' && (
        <div className="mb-6 rounded-lg border border-warn/40 bg-warn-soft p-4">
          <p className="font-medium">Check this club before approving it</p>
          <p className="mt-1 text-[14px]">
            Call {c.contactName} on{' '}
            <a href={`tel:${c.mobile}`} className="inline-flex items-center gap-1 font-semibold underline">
              <Phone className="size-3.5" aria-hidden />
              {formatMobile(c.mobile)}
            </a>{' '}
            and make sure they really run {c.name}.{c.website ? ' Their website may help too.' : ''}
          </p>
          {!c.contactConfirmed && <p className="mt-1 text-[14px]">{c.contactName} hasn’t confirmed their email address yet.</p>}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button loading={decide.isPending} onClick={() => decide.mutate({ decision: 'approve' })}>
              Approve
            </Button>
            <Button variant="secondary" onClick={() => setDialog('reject')}>
              Reject
            </Button>
          </div>
          {decide.isError && (
            <div className="mt-3">
              <Alert>{errorMessage(decide.error)}</Alert>
            </div>
          )}
        </div>
      )}

      <dl className="grid max-w-[640px] grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-[14px]">
        <dt className="text-muted">Contact</dt>
        <dd>
          {c.contactName}, <a href={`mailto:${c.contactEmail}`} className="hover:underline">{c.contactEmail}</a>
        </dd>
        <dt className="text-muted">Mobile</dt>
        <dd className="tnum">
          <a href={`tel:${c.mobile}`} className="hover:underline">
            {formatMobile(c.mobile)}
          </a>
        </dd>
        <dt className="text-muted">Where</dt>
        <dd>{[c.suburb, c.state].filter(Boolean).join(' ') || '–'}</dd>
        <dt className="text-muted">Website</dt>
        <dd>
          {c.website ? (
            <a href={c.website} target="_blank" rel="noreferrer noopener" className="text-action hover:underline">
              {c.website.replace(/^https?:\/\//, '')}
            </a>
          ) : (
            '–'
          )}
        </dd>
        <dt className="text-muted">Registered</dt>
        <dd>
          {formatDate(c.createdAt)}
          {c.registeredBy ? ` by ${c.registeredBy}` : ' by Hoopify'}
        </dd>
        {c.decidedAt && (
          <>
            <dt className="text-muted">Last decision</dt>
            <dd>
              {STATUS[c.status].label} by {c.decidedBy}, {formatDate(c.decidedAt)}
              {c.decisionNote ? ` (${c.decisionNote})` : ''}
            </dd>
          </>
        )}
        <dt className="text-muted">Teams</dt>
        <dd className="tnum">{c.teamCount}</dd>
        <dt className="text-muted">Runs it</dt>
        <dd>
          {c.admins.length ? (
            <ul className="space-y-0.5">
              {c.admins.map((a) => (
                <li key={a.email}>
                  {a.name} <span className="text-muted">({a.email}, {CLUB_ROLE_LABELS[a.role].toLowerCase()})</span>
                </li>
              ))}
            </ul>
          ) : (
            <span className="text-muted">Nobody yet: the invite to {c.contactEmail} hasn’t been accepted.</span>
          )}
        </dd>
      </dl>

      <div className="mt-6 flex flex-wrap gap-2 border-t border-line pt-5">
        {c.status === 'approved' && (
          <Button variant="secondary" onClick={() => setDialog('suspend')}>
            Suspend club
          </Button>
        )}
        {c.status === 'suspended' && <Button onClick={() => setDialog('restore')}>Restore club</Button>}
        {c.status === 'rejected' && (
          <Button variant="secondary" loading={decide.isPending} onClick={() => decide.mutate({ decision: 'approve' })}>
            Approve after all
          </Button>
        )}
      </div>

      <ReasonDialog open={dialog === 'reject'} onClose={() => setDialog(null)} title={`Reject ${c.name}?`} confirm="Reject club" tone="danger" run={(note) => decide.mutateAsync({ decision: 'reject', note })}>
        The reason goes in the email to {c.contactName}.
      </ReasonDialog>
      <ReasonDialog open={dialog === 'suspend'} onClose={() => setDialog(null)} title={`Suspend ${c.name}?`} confirm="Suspend club" tone="danger" run={(note) => decide.mutateAsync({ decision: 'suspend', note })}>
        Its admins and registrars can’t use the club pages until you restore it. Its teams carry on as normal. The reason goes in the email to them.
      </ReasonDialog>
      <ReasonDialog open={dialog === 'restore'} onClose={() => setDialog(null)} title={`Restore ${c.name}?`} confirm="Restore club" run={(note) => decide.mutateAsync({ decision: 'restore', note })} />
    </>
  );
}
